import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, TextField,
  Select, MenuItem, InputAdornment, Divider, Link,
  IconButton, Chip, Tooltip,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ClearIcon from '@mui/icons-material/Clear';
import { buildFieldConfig } from '../../utils/setupDataMapper';
import TirePicker from '../tires/TirePicker';

// ─────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────

const SectionLabel = ({ label }) => (
  <Typography variant="caption" sx={{
    display: 'block', pt: 1, pb: '3px',
    fontWeight: 700, letterSpacing: '0.09em',
    textTransform: 'uppercase', color: 'text.disabled',
    borderBottom: '1px solid', borderColor: 'divider', mb: '4px',
  }}>
    {label}
  </Typography>
);

/** Griglia 2 colonne per i campi input */
const FieldGrid = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px' }}>
    {children}
  </Box>
);

/** TextField compatto per tutte le sezioni */
const F = ({ label, value, onChange, unit, type = 'text', multiline, rows, disabled, select, children, ...rest }) => (
  <TextField
    label={label}
    value={value ?? ''}
    onChange={onChange}
    size="small"
    fullWidth
    type={type}
    disabled={disabled}
    multiline={multiline}
    rows={rows}
    inputProps={{ step: type === 'number' ? 'any' : undefined }}
    InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
    select={select}
    {...rest}
  >
    {children}
  </TextField>
);

const SectionDivider = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
    <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'text.disabled', fontSize: 9, flexShrink: 0 }}>
      {label}
    </Typography>
    <Divider sx={{ flex: 1 }} />
  </Box>
);

// ─────────────────────────────────────────────────────────────────
// CORNER FORM CARD
// ─────────────────────────────────────────────────────────────────

/**
 * Card angolo editable. Mostra solo i campi abilitati nel template.
 *
 * Props:
 *   tag         – 'FL' | 'FR' | 'RL' | 'RR'
 *   axle        – oggetto newSetupData.anteriore o .posteriore
 *   gomme       – oggetto newSetupData.gomme_ant_sx ecc.
 *   aeroData    – newSetupData.aero (per altezza target front)
 *   weightsData – newSetupData.weights (per peso angolo)
 *   onChange(section, field, value)
 *   onNestedChange(section, subKey, field, value)
 *   onTireChange(tireKey, field, value)
 *   axleKey     – 'anteriore' | 'posteriore'
 *   tireKey     – 'gomme_ant_sx' ecc.
 *   hasCaster
 *   fieldConfig – buildFieldConfig(template.modules)
 */
const CornerFormCard = ({
  tag, axle, gomme, aeroData, weightsData,
  onChange, onNestedChange, onTireChange,
  onOpenTirePicker, canPickTire,
  axleKey, tireKey,
  hasCaster, fieldConfig, t,
}) => {
  const { t: tTires } = useTranslation('tires');
  const { isHidden, isModuleActive, customFields } = fieldConfig;
  const amm = axle?.ammortizzatori || {};

  const show = (moduleKey, fieldKey) => {
    if (!isModuleActive(moduleKey)) return false;
    return !isHidden(fieldKey);
  };

  const cornerLabel = {
    FL: t('quadrant_fl', { defaultValue: 'Ant. Sinistra' }),
    FR: t('quadrant_fr', { defaultValue: 'Ant. Destra' }),
    RL: t('quadrant_rl', { defaultValue: 'Post. Sinistra' }),
    RR: t('quadrant_rr', { defaultValue: 'Post. Destra' }),
  }[tag];
  const toeDefault = axleKey === 'anteriore' ? 'out' : 'in';
  const side = tag === 'FL' || tag === 'RL' ? 'SX' : 'DX';
  const weightKey = `corner${tag}`;
  const rideHeightField = tag === 'FL' ? 'altezzaTargetSX' : tag === 'FR' ? 'altezzaTargetDX' : null;
  const cornerCustomFields = (customFields[tag] || []);

  const showGeo = show('alignment', `convergenza${side}`) || show('alignment', `camber${side}`) ||
    (hasCaster && show('alignment', `caster${side}`)) ||
    (rideHeightField && show('aero', rideHeightField));
  const showSprings = show('mechanical_grip', `molla${side}`) || show('mechanical_grip', `rigidezzaBumpstop${side}`) || show('mechanical_grip', `distanzaBumpstop${side}`);
  const showDampers = show('dampers', `bump${side}`) || show('dampers', `rebound${side}`) || show('dampers', `fastBump${side}`) || show('dampers', `fastRebound${side}`);
  const showTyre = isModuleActive('strategy_tires');
  const showWeight = show('weights', weightKey);

  if (!showGeo && !showSprings && !showDampers && !showTyre && !showWeight && cornerCustomFields.length === 0) {
    return null;
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, backgroundColor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography fontWeight={800} fontSize={17} lineHeight={1}>{tag}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>{cornerLabel}</Typography>
        </Box>
        {showTyre && (
          <Typography variant="caption" color="text.secondary">
            {gomme?.pFreddo ? `${gomme.pFreddo} psi` : tTires('pressure_fallback_short')}
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5 }}>

        {/* GEOMETRIA */}
        {showGeo && (
          <>
            <SectionLabel label={t('cc_section_geometry')} />
            <FieldGrid>
              {show('alignment', `convergenza${side}`) && (
                <Box sx={{ display: 'flex', gap: 0.5, gridColumn: '1 / -1' }}>
                  <F
                    label={t('cc_label_toe')}
                    type="number"
                    value={axle?.[`convergenza${side}`]}
                    onChange={(e) => onChange(axleKey, `convergenza${side}`, e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  {show('alignment', `toeInOrOut${side}`) && (
                    <Select
                      size="small"
                      value={axle?.[`toeInOrOut${side}`] || toeDefault}
                      onChange={(e) => onChange(axleKey, `toeInOrOut${side}`, e.target.value)}
                      sx={{ width: 80 }}
                    >
                      <MenuItem value="out">{t('cc_toe_dir_out')}</MenuItem>
                      <MenuItem value="in">{t('cc_toe_dir_in')}</MenuItem>
                    </Select>
                  )}
                </Box>
              )}
              {show('alignment', `camber${side}`) && (
                <F label={t('cc_label_camber')} type="number" unit="°"
                  value={axle?.[`camber${side}`]}
                  onChange={(e) => onChange(axleKey, `camber${side}`, e.target.value)} />
              )}
              {hasCaster && show('alignment', `caster${side}`) && (
                <F label={t('cc_label_caster')} type="number" unit="°"
                  value={axle?.[`caster${side}`]}
                  onChange={(e) => onChange(axleKey, `caster${side}`, e.target.value)} />
              )}
              {rideHeightField && show('aero', rideHeightField) && (
                <F label={t('cc_label_height')} type="number" unit="mm"
                  value={aeroData?.[rideHeightField]}
                  onChange={(e) => onChange('aero', rideHeightField, e.target.value)} />
              )}
            </FieldGrid>
          </>
        )}

        {/* MOLLA & BUMPSTOP */}
        {showSprings && (
          <>
            <SectionLabel label={t('cc_section_springs')} />
            <FieldGrid>
              {show('mechanical_grip', `molla${side}`) && (
                <F label={t('cc_label_spring')}
                  value={axle?.[`molla${side}`]}
                  onChange={(e) => onChange(axleKey, `molla${side}`, e.target.value)} />
              )}
              {show('mechanical_grip', `rigidezzaBumpstop${side}`) && (
                <F label={t('cc_label_bs_rate')}
                  value={axle?.[`rigidezzaBumpstop${side}`]}
                  onChange={(e) => onChange(axleKey, `rigidezzaBumpstop${side}`, e.target.value)} />
              )}
              {show('mechanical_grip', `distanzaBumpstop${side}`) && (
                <F label={t('cc_label_bs_range')} type="number" unit="mm"
                  value={axle?.[`distanzaBumpstop${side}`]}
                  onChange={(e) => onChange(axleKey, `distanzaBumpstop${side}`, e.target.value)} />
              )}
            </FieldGrid>
          </>
        )}

        {/* AMMORTIZZATORI */}
        {showDampers && (
          <>
            <SectionLabel label={t('section_dampers')} />
            <FieldGrid>
              {show('dampers', `bump${side}`) && (
                <F label={t('cc_label_bump_slow')} type="number"
                  value={amm?.[`bump${side}`]}
                  onChange={(e) => onNestedChange(axleKey, 'ammortizzatori', `bump${side}`, e.target.value)} />
              )}
              {show('dampers', `rebound${side}`) && (
                <F label={t('cc_label_rebound_slow')} type="number"
                  value={amm?.[`rebound${side}`]}
                  onChange={(e) => onNestedChange(axleKey, 'ammortizzatori', `rebound${side}`, e.target.value)} />
              )}
              {show('dampers', `fastBump${side}`) && (
                <F label={t('cc_label_bump_fast')} type="number"
                  value={amm?.[`fastBump${side}`]}
                  onChange={(e) => onNestedChange(axleKey, 'ammortizzatori', `fastBump${side}`, e.target.value)} />
              )}
              {show('dampers', `fastRebound${side}`) && (
                <F label={t('cc_label_rebound_fast')} type="number"
                  value={amm?.[`fastRebound${side}`]}
                  onChange={(e) => onNestedChange(axleKey, 'ammortizzatori', `fastRebound${side}`, e.target.value)} />
              )}
            </FieldGrid>
          </>
        )}

        {/* GOMMA */}
        {showTyre && (
          <>
            <SectionLabel label={t('cc_section_tyre')} />
            <FieldGrid>
              <F label={t('cc_label_p_cold')} type="number" unit="psi"
                value={gomme?.pFreddo}
                onChange={(e) => onTireChange(tireKey, 'pFreddo', e.target.value)} />
              <F label={t('cc_label_p_hot')} type="number" unit="psi"
                value={gomme?.pCaldoTarget}
                onChange={(e) => onTireChange(tireKey, 'pCaldoTarget', e.target.value)} />
              <TextField
                label={t('cc_label_tyre_code')}
                size="small"
                fullWidth
                variant="outlined"
                value={gomme?.codicePneumatico || ''}
                onChange={(e) => onTireChange(tireKey, 'codicePneumatico', e.target.value)}
                InputProps={canPickTire ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={tTires('tooltip_pick_or_scan')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => onOpenTirePicker(tag, tireKey)}
                          edge="end"
                        >
                          <QrCodeScannerIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                } : undefined}
              />
            </FieldGrid>
            {/* Chip gomma collegata dal database */}
            {gomme?.tireId && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${gomme.tireBrand || tTires('tire_chip_fallback_brand')}${gomme.tireCompound ? ' · ' + gomme.tireCompound : ''} ${tTires('tire_chip_db_suffix')}`}
                  onDelete={() => {
                    onTireChange(tireKey, 'tireId',       '');
                    onTireChange(tireKey, 'tireBrand',    '');
                    onTireChange(tireKey, 'tireCompound', '');
                  }}
                  deleteIcon={<ClearIcon />}
                />
              </Box>
            )}
          </>
        )}

        {/* PESO ANGOLO */}
        {showWeight && (
          <>
            <SectionLabel label={t('cc_corner_weight')} />
            <F label={tag} type="number" unit="kg"
              value={weightsData?.[weightKey]}
              onChange={(e) => onChange('weights', weightKey, e.target.value)} />
          </>
        )}

        {/* CUSTOM FIELDS */}
        {cornerCustomFields.length > 0 && (
          <>
            <SectionLabel label="Custom" />
            <FieldGrid>
              {cornerCustomFields.map(cf => (
                <F
                  key={cf.key}
                  label={cf.label}
                  type={cf.type === 'number' ? 'number' : 'text'}
                  value={axle?.[cf.key] ?? ''}
                  onChange={(e) => onChange(axleKey, cf.key, e.target.value)}
                />
              ))}
            </FieldGrid>
          </>
        )}

      </Box>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────
// CHASSIS FORM PANEL
// ─────────────────────────────────────────────────────────────────

const GroupPanel = ({ title, children }) => (
  <Box>
    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'text.disabled', borderBottom: '1px solid', borderColor: 'divider', pb: '3px', fontSize: 9 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px' }}>
      {children}
    </Box>
  </Box>
);

const ChassisFormPanel = ({ setupData, fieldConfig, onChange, onNestedChange, t, totalCalculatedWeight_HelperText }) => {
  const { isModuleActive } = fieldConfig;
  const show = (moduleKey, fieldKey) => isModuleActive(moduleKey) && !fieldConfig.isHidden(fieldKey);

  const ant  = setupData.anteriore || {};
  const post = setupData.posteriore || {};
  const diff = setupData.differenziale || {};
  const freni = setupData.freni || {};
  const aero = setupData.aero || {};
  const el   = setupData.elettronica || {};
  const w    = setupData.weights || {};
  const str  = setupData.strategia || {};
  const fb   = setupData.feedback || {};

  const hasTelaio   = isModuleActive('mechanical_grip') || isModuleActive('aero') || isModuleActive('electronics');
  const hasPesi     = isModuleActive('weights');
  const hasStrategia = isModuleActive('strategy_tires');
  const hasFeedback  = isModuleActive('feedback');

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mt: 1.5 }}>

      {/* ═══ TELAIO ═══ */}
      {hasTelaio && (
        <>
          <SectionDivider label={t('cc_tab_chassis')} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(960px, 1fr))' }, gap: 2 }}>

            {isModuleActive('mechanical_grip') && (
              <GroupPanel title={t('cc_group_arb')}>
                <F label={t('cc_arb_front')} value={ant.barra} onChange={(e) => onChange('anteriore', 'barra', e.target.value)} />
                <F label={t('cc_arb_rear')} value={post.barra} onChange={(e) => onChange('posteriore', 'barra', e.target.value)} />
              </GroupPanel>
            )}

            {isModuleActive('mechanical_grip') && (
              <GroupPanel title={t('cc_group_diff')}>
                {show('mechanical_grip', 'precarico') && (
                  <F label={t('field_diff_preload')} type="number" unit="Nm" value={diff.precarico} onChange={(e) => onChange('differenziale', 'precarico', e.target.value)} />
                )}
                {show('mechanical_grip', 'bloccaggio') && (
                  <F label={t('field_diff_lock')} type="number" unit="%" value={diff.bloccaggio} onChange={(e) => onChange('differenziale', 'bloccaggio', e.target.value)} />
                )}
              </GroupPanel>
            )}

            {isModuleActive('mechanical_grip') && (
              <GroupPanel title={t('cc_group_brakes')}>
                {show('mechanical_grip', 'bilanciamentoFreni') && (
                  <F label={t('field_brake_bias')} type="number" unit="%" value={freni.bilanciamentoFreni} onChange={(e) => onChange('freni', 'bilanciamentoFreni', e.target.value)} />
                )}
                {show('mechanical_grip', 'potenzaFreni') && (
                  <F label={t('field_brake_power')} type="number" unit="%" value={freni.potenzaFreni} onChange={(e) => onChange('freni', 'potenzaFreni', e.target.value)} />
                )}
                {show('mechanical_grip', 'rapportoSterzo') && (
                  <F label={t('field_steering_ratio')} type="number" value={freni.rapportoSterzo} onChange={(e) => onChange('freni', 'rapportoSterzo', e.target.value)} />
                )}
              </GroupPanel>
            )}

            {isModuleActive('aero') && (
              <GroupPanel title={t('section_aero')}>
                {show('aero', 'alaPosteriore') && (
                  <F label={t('field_wing_rear')} value={aero.alaPosteriore} onChange={(e) => onChange('aero', 'alaPosteriore', e.target.value)} />
                )}
                {show('aero', 'splitterAnteriore') && (
                  <F label={t('field_splitter')} value={aero.splitterAnteriore} onChange={(e) => onChange('aero', 'splitterAnteriore', e.target.value)} />
                )}
                {show('aero', 'condottiFrenoAnteriori') && (
                  <F label={t('field_brake_ducts_front')} value={aero.condottiFrenoAnteriori} onChange={(e) => onChange('aero', 'condottiFrenoAnteriori', e.target.value)} />
                )}
                {show('aero', 'condottiFrenoPosteriori') && (
                  <F label={t('field_brake_ducts_rear')} value={aero.condottiFrenoPosteriori} onChange={(e) => onChange('aero', 'condottiFrenoPosteriori', e.target.value)} />
                )}
              </GroupPanel>
            )}

            {isModuleActive('electronics') && (
              <GroupPanel title={t('section_electronics')}>
                {show('electronics', 'tc1') && (
                  <F label={t('field_electronics_tc1')} value={el.tc1} onChange={(e) => onChange('elettronica', 'tc1', e.target.value)} />
                )}
                {show('electronics', 'tc2') && (
                  <F label={t('field_electronics_tc2')} value={el.tc2} onChange={(e) => onChange('elettronica', 'tc2', e.target.value)} />
                )}
                {show('electronics', 'abs') && (
                  <F label={t('field_electronics_abs')} value={el.abs} onChange={(e) => onChange('elettronica', 'abs', e.target.value)} />
                )}
                {show('electronics', 'mappaECU') && (
                  <F label={t('field_electronics_ecu')} value={el.mappaECU} onChange={(e) => onChange('elettronica', 'mappaECU', e.target.value)} />
                )}
              </GroupPanel>
            )}
          </Box>
        </>
      )}

      {/* ═══ PESI ═══ */}
      {hasPesi && (
        <>
          <SectionDivider label={t('cc_tab_weights')} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(960px, 1fr))' }, gap: 2 }}>
            <GroupPanel title={t('cc_group_weights_base')}>
              {show('weights', 'dryWeight') && (
                <F label={t('field_dry_weight')} type="number" unit="kg" value={w.dryWeight} disabled helperText={t('field_dry_weight_helper')} />
              )}
              {show('weights', 'driverWeight') && (
                <F label={t('field_driver_weight')} type="number" unit="kg" value={w.driverWeight} disabled helperText={t('field_driver_weight_helper')} />
              )}
              {show('weights', 'fuelLiters') && (
                <F label={t('field_fuel_liters')} type="number" unit="L" value={w.fuelLiters} onChange={(e) => onChange('weights', 'fuelLiters', e.target.value)} />
              )}
              {show('weights', 'minRegWeight') && (
                <F label={t('field_min_reg_weight')} type="number" unit="kg" value={w.minRegWeight} onChange={(e) => onChange('weights', 'minRegWeight', e.target.value)} />
              )}
            </GroupPanel>
            <GroupPanel title={t('cc_group_weights_total')}>
              <F label={t('field_total_weight')} type="number" unit="kg" value={w.totalWeight}
                onChange={(e) => onChange('weights', 'totalWeight', e.target.value)}
                helperText={totalCalculatedWeight_HelperText} />
              <F label={t('field_front_weight')} type="number" unit="kg" value={w.totalFrontWeight} onChange={(e) => onChange('weights', 'totalFrontWeight', e.target.value)} />
              <F label={t('field_rear_weight')} type="number" unit="kg" value={w.totalRearWeight} onChange={(e) => onChange('weights', 'totalRearWeight', e.target.value)} />
              <F label={t('field_weight_dist')} type="number" unit="%" value={w.weightRip} onChange={(e) => onChange('weights', 'weightRip', e.target.value)} />
            </GroupPanel>
            <GroupPanel title={t('cc_group_ballast')}>
              <F label={t('field_ballast_applied')} type="number" unit="kg" value={w.ballast} onChange={(e) => onChange('weights', 'ballast', e.target.value)} />
            </GroupPanel>
          </Box>
        </>
      )}

      {/* ═══ STRATEGIA ═══ */}
      {hasStrategia && (
        <>
          <SectionDivider label={t('cc_tab_strategy')} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(960px, 1fr))' }, gap: 2 }}>
            <GroupPanel title={t('cc_group_fuel')}>
              {show('strategy_tires', 'carburanteIniziale') && (
                <F label={t('field_strategy_fuel_start')} type="number" unit="L" value={str.carburanteIniziale} onChange={(e) => onChange('strategia', 'carburanteIniziale', e.target.value)} />
              )}
              <F label={t('field_strategy_fuel_consumption')} type="number" unit="L/giro" value={diff.fuelConsumption} onChange={(e) => onChange('differenziale', 'fuelConsumption', e.target.value)} />
            </GroupPanel>
            <GroupPanel title={t('cc_group_brake_pads')}>
              {show('strategy_tires', 'pastiglieFrenoAnteriori') && (
                <F label={t('field_strategy_brakes_front')} type="number" value={freni.pastiglieFrenoAnteriori} onChange={(e) => onChange('freni', 'pastiglieFrenoAnteriori', e.target.value)} />
              )}
              {show('strategy_tires', 'pastiglieFrenoPosteriori') && (
                <F label={t('field_strategy_brakes_rear')} type="number" value={freni.pastiglieFrenoPosteriori} onChange={(e) => onChange('freni', 'pastiglieFrenoPosteriori', e.target.value)} />
              )}
            </GroupPanel>
            {show('strategy_tires', 'strategiaPit') && (
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <F label={t('field_strategy_pit_plan')} value={str.strategiaPit} onChange={(e) => onChange('strategia', 'strategiaPit', e.target.value)} multiline rows={3} />
              </Box>
            )}
          </Box>
        </>
      )}

      {/* ═══ NOTE & FEEDBACK ═══ */}
      {hasFeedback && (
        <>
          <SectionDivider label={t('cc_tab_notes')} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px' }}>
              {show('feedback', 'bestLap') && (
                <F label={t('field_best_lap')} value={fb.bestLap} onChange={(e) => onChange('feedback', 'bestLap', e.target.value)} />
              )}
              {show('feedback', 'listaTempi') && (
                <F label={t('field_times_list')} value={fb.listaTempi} onChange={(e) => onChange('feedback', 'listaTempi', e.target.value)} multiline rows={3} sx={{ gridColumn: '1 / -1' }} />
              )}
              {show('feedback', 'linkTelemetria') && (
                <F label={t('field_telemetry_link')} type="url" value={fb.linkTelemetria} onChange={(e) => onChange('feedback', 'linkTelemetria', e.target.value)} sx={{ gridColumn: '1 / -1' }} />
              )}
              {show('feedback', 'linkVideo') && (
                <F label={t('field_video_link')} type="url" value={fb.linkVideo} onChange={(e) => onChange('feedback', 'linkVideo', e.target.value)} sx={{ gridColumn: '1 / -1' }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {show('feedback', 'noteTecnico') && (
                <F label={t('field_tech_notes')} value={fb.noteTecnico} onChange={(e) => onChange('feedback', 'noteTecnico', e.target.value)} multiline rows={3} />
              )}
              {show('feedback', 'feedbackPilota') && (
                <F label={t('field_driver_feedback')} value={fb.feedbackPilota} onChange={(e) => onChange('feedback', 'feedbackPilota', e.target.value)} multiline rows={3} />
              )}
            </Box>
          </Box>
        </>
      )}

    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────
// CAR-CENTRIC FORM VIEW — ASSEMBLER
// ─────────────────────────────────────────────────────────────────

/**
 * Vista car-centric per il form di inserimento setup.
 * Sostituisce il render accordion dei moduli LEGO.
 * Scrive nelle stesse chiavi Firestore del vecchio form — retrocompatibile al 100%.
 *
 * Props:
 *   setupData                     – newSetupData (stato del form)
 *   setupTemplate                 – template con modules[]
 *   handleChange(section, field, value)
 *   handleNestedChange(section, subKey, field, value)
 *   handleTireChange(tireKey, field, value)
 *   t                             – useTranslation('setup')
 *   totalCalculatedWeight_HelperText
 */
const CarCentricFormView = ({
  setupData, setupTemplate,
  handleChange, handleNestedChange, handleTireChange,
  t, totalCalculatedWeight_HelperText,
  teamId, userId, vehicleId, vehicles = [],
}) => {
  const fieldConfig = useMemo(() => buildFieldConfig(setupTemplate?.modules), [setupTemplate]);

  // Stato per TirePicker (quale angolo sta scegliendo)
  const [pickerOpen, setPickerOpen]       = useState(false);
  const [pickerCorner, setPickerCorner]   = useState({ tag: '', tireKey: '' });

  // Lista gomme assegnate IN ALTRI angoli del setup corrente (da escludere dal picker)
  const TIRE_KEYS = ['gomme_ant_sx', 'gomme_ant_dx', 'gomme_post_sx', 'gomme_post_dx'];
  const excludeTireIds = useMemo(() => (
    TIRE_KEYS
      .filter(k => k !== pickerCorner.tireKey)
      .map(k => setupData?.[k]?.tireId)
      .filter(Boolean)
  ), [setupData, pickerCorner.tireKey]);

  const openTirePicker = (tag, tireKey) => {
    setPickerCorner({ tag, tireKey });
    setPickerOpen(true);
  };

  const handleTireSelected = (tire) => {
    const { tireKey } = pickerCorner;
    if (!tireKey || !tire) return;
    handleTireChange(tireKey, 'codicePneumatico', tire.barcode || '');
    handleTireChange(tireKey, 'tireId',           tire.id || '');
    handleTireChange(tireKey, 'tireBrand',        tire.brand || '');
    handleTireChange(tireKey, 'tireCompound',     tire.compound || '');
    handleTireChange(tireKey, 'tireDimensions',   tire.dimensions || '');
  };

  const cornerProps = (tag, axleKey, tireKey, hasCaster) => ({
    tag, axleKey, tireKey, hasCaster,
    axle:        setupData[axleKey],
    gomme:       setupData[tireKey],
    aeroData:    setupData.aero,
    weightsData: setupData.weights,
    onChange:    handleChange,
    onNestedChange: handleNestedChange,
    onTireChange:   handleTireChange,
    onOpenTirePicker: openTirePicker,
    canPickTire:    !!teamId, // mostra il bottone solo se abbiamo i parametri
    fieldConfig,
    t,
  });

  return (
    <Box sx={{ mt: 2 }}>
      {/* Etichette asse — solo desktop */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'space-between', px: 0.5, mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 9 }}>← Sinistra</Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 9 }}>Anteriore ↑</Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 9 }}>Destra →</Typography>
      </Box>

      {/* 4 angoli — griglia 2×2 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
        <CornerFormCard {...cornerProps('FL', 'anteriore', 'gomme_ant_sx',  true)} />
        <CornerFormCard {...cornerProps('FR', 'anteriore', 'gomme_ant_dx',  true)} />
        <CornerFormCard {...cornerProps('RL', 'posteriore', 'gomme_post_sx', false)} />
        <CornerFormCard {...cornerProps('RR', 'posteriore', 'gomme_post_dx', false)} />
      </Box>

      {/* Pannello chassis + pesi + strategia + note */}
      <ChassisFormPanel
        setupData={setupData}
        fieldConfig={fieldConfig}
        onChange={handleChange}
        onNestedChange={handleNestedChange}
        t={t}
        totalCalculatedWeight_HelperText={totalCalculatedWeight_HelperText}
      />

      {/* TirePicker — dialog per scegliere/scansionare una gomma */}
      <TirePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleTireSelected}
        position={pickerCorner.tag}
        teamId={teamId}
        userId={userId}
        vehicleId={vehicleId}
        vehicles={vehicles}
        excludeTireIds={excludeTireIds}
      />
    </Box>
  );
};

export default CarCentricFormView;
